import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { IoClose as Close } from 'react-icons/io5';
import { BsTrash3 as TrashIcon } from 'react-icons/bs';
import { FiAlertTriangle, FiClock } from 'react-icons/fi';
import { LuShieldAlert } from "react-icons/lu";
import LeadsService from '@Helpers/URLHelpers/LeadsService';
import ErrorHandler from '@Root/ErrorHandler';
import './DeleteLeadsForm.scss';

export default function DeleteLeadsForm(props) {

    const [cursor, setCursor] = useState('auto');
    const [deleting, setDeleting] = useState(false);
    const leadsService = new LeadsService();
    const errorHandler = new ErrorHandler();
    const className = 'deleteLeadsForm';
    const leadName = props?.lead?.name || 'Untitled lead';

    const timeout = (delay) => {
        return new Promise((resolve) => setTimeout(resolve, delay));
    };

    const handleDelete = async () => {
        setCursor('wait');
        setDeleting(true);
        try {
            const response = await leadsService.deleteById(props?.lead.id);
            if (response.status < 400) {
                await timeout(500);
                props.reloadData();
                props.closePopup();
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setDeleting(false);
        setCursor('auto');
    };

    return (
        <Dialog
            open={props.open}
            onClose={deleting ? undefined : props.closePopup}
            maxWidth="sm"
            fullWidth
            className={className}
            PaperProps={{
                className: `${className}__paper`,
            }}
            style={{ cursor }}
        >
            <DialogTitle className={`${className}__header`}>
                <Box className={`${className}__header-content`}>
                    <Box className={`${className}__icon`}>
                        <TrashIcon />
                    </Box>

                    <Box className={`${className}__heading`}>
                        <Typography
                            className={`${className}__eyebrow`}
                        >
                            Delete Lead
                        </Typography>

                        <Typography
                            className={`${className}__title`}
                        >
                            Permanently delete this lead ?
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={props.closePopup}
                    disabled={deleting}
                    className={`${className}__close`}
                    size="small"
                    aria-label="Close"
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent className={`${className}__content`}>
                <Box className={`${className}__template-card`}>
                    <Box className={`${className}__template-icon`}>
                        <LuShieldAlert />
                    </Box>

                    <Box className={`${className}__template-info`}>
                        <Typography
                            className={`${className}__template-label`}
                        >
                            Lead
                        </Typography>

                        <Typography
                            className={`${className}__template-name`}
                            title={leadName}
                        >
                            {leadName}
                        </Typography>
                    </Box>
                </Box>

                <Box className={`${className}__warning`}>
                    <Box className={`${className}__warning-icon`}>
                        <FiAlertTriangle />
                    </Box>

                    <Box className={`${className}__warning-content`}>
                        <Typography
                            className={`${className}__warning-title`}
                        >
                            This action cannot be undone
                        </Typography>

                        <Typography
                            className={`${className}__warning-text`}
                        >
                            Once deleted, this lead will no longer be
                            available in the system.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions className={`${className}__footer`}>
                <Button
                    onClick={props.closePopup}
                    disabled={deleting}
                    variant="text"
                    className={`${className}__cancel`}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    startIcon={
                        deleting ? (
                            <CircularProgress
                                size={17}
                                color="inherit"
                            />
                        ) : (
                            <TrashIcon />
                        )
                    }
                    onClick={handleDelete}
                    disabled={deleting}
                    className={`${className}__delete`}
                >
                    {deleting ? 'Deleting...' : 'Delete Lead'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
